import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./overviewV2.html";
import { schemas } from '@oneappexchange/oaf-schema';
import { PopoverHorizontalOrientation, PopoverVerticalOrientation } from 'wds/popover';
import { FeatureFlags } from '~/modules/feature_flags';
import { generateBadgeTooltips, generateLocalizedBadge, generateSimpleBadge } from '~/modules/helpers/badgeHelper';
import { selectCompatibleWith, selectLanguages, selectRequiredProducts, selectSalesforceEditions } from '~/modules/helpers/compatibilityHelper';
import { getListingPublisher, selectProviderAchievements, selectSocialImpactFeatures } from '~/modules/helpers/listingHelper';
import { selectSupportFeatures } from '~/modules/helpers/selectSupportedFeatures';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { isConsultantListing, selectExtensionListing } from '~/modules/utilities/listing_utils';
import { buildTooltipAriaLabel, parseNewLines } from '~/modules/utilities/text';

/**
 * What `listingDetailV2` actually passes to `carouselItems` — its
 * `formatCarouselItem` tags each entry with `pluginType`. Carousel media only:
 * Demo plugin items feed the Watch Demo banner through `hasDemoItem` /
 * `watchDemoUrl`, not this list. Not `LightBoxItem`: that is the flattened
 * shape `listing-carousel` derives downstream for the lightbox.
 */

/**
 * v2 Overview panel — embedded by `pages/listingDetailV2` in place of v1's
 * `listing-overview`.
 *
 * This began as a STANDALONE WHOLESALE COPY of the former v1 `listing/overview`
 * (not a subclass). We deliberately did NOT `extend Overview`: unlike the header
 * rebrand (which has `headerWrapper` selecting old-vs-new), Overview had no
 * wrapper — the choice of v1-vs-v2 was made by whichever *page* embedded it.
 * Editing v1 in place, or making v2 inherit from it, would have left the frozen
 * v1 page silently rendering v2 code (or coupled retirement to an
 * inheritance-unwind). Copying kept v1 untouched and made retirement a pure
 * delete. v1 `listing/overview` has since been deleted at 100% rollout
 * (W-24557579), so this is now the sole Overview implementation.
 *
 * Tradeoff we accept: `git blame` on this file threads back only to the fork
 * commit; use `git blame -C -C` / `git log --follow` to reach the pre-fork
 * history, which also stays reachable via `git log -- <v1 path>` after v1 is
 * deleted.
 *
 * The one intentional divergence from the original v1 is marked "v2:" below.
 *
 * NOTE: this component is intentionally INCOMPLETE. It is shipped as a working
 * skeleton + the canonical example of how to fork a v1 panel component for v2,
 * so the same recipe can be applied to the remaining tabs. See
 * `docs/listing-detail-v2.md` ("Overview v2") for the outstanding work
 * (Features carousel, demo card).
 */
class OverviewV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    // Carousel inputs — the page (`listingDetailV2`) owns the data derivation and
    // passes these down so the media/demo carousel now lives inside the Overview
    // tab rather than above the tab group. See docs/W-23636498-overviewv2-changes.md.
    this.carouselItems = null;
    this.isMobilePhone = false;
    this.hasDemoLeadsEnabled = false;
    this.hasDemoItem = false;
    this.watchDemoUrl = '';
    this.watchDemoPreviewUrl = '';
    /**
     * Category / competency and Compatibility badge lists, recomputed off the render
     * path (listing setter + i18nCallback) so the getters the template reads stay pure
     * and never mutate reactive state mid-render.
     */
    this.categoryGroups = {
      businessNeeds: [],
      industries: [],
      targetPersonas: [],
      supportedFeatures: [],
      supportedFeaturesTooltip: [],
      industryCompetencies: [],
      productCompetencies: [],
      geographicFocus: [],
      languages: []
    };
    // --- Compatibility (ported from listing/specs) ---
    this.compatibilityBadges = {
      requiredProducts: [],
      compatibleWith: [],
      salesforceEditions: [],
      supportedLanguages: []
    };
  }
  /** The listing under display. The setter recomputes the Categories / Compatibility badges off the render path. */
  get listing() {
    return this._listing;
  }
  set listing(value) {
    this._listing = value;
    this.recomputeBadges();
  }
  i18nCallback(i18n) {
    this.i18n = i18n;
    this.recomputeBadges();
  }
  get hasCarouselItems() {
    return (this.carouselItems?.length ?? 0) > 0;
  }
  get highlights() {
    return this.forceListing?.highlights?.filter(Boolean);
  }
  get forceListing() {
    return selectExtensionListing(this.listing);
  }
  get hasHighlights() {
    return this.highlights?.length > 0;
  }

  /**
   * The "Features" section (Figma node 7609:52115) groups, in order, the short
   * listing description + media/demo carousel (v1's "carousel-wrapper" unit),
   * the numbered highlight cards, and finally the full description brought over
   * from the More Details "Additional Details" block — all under one "Features"
   * heading. Render the section when any of those pieces is present.
   */
  get hasFeaturesSection() {
    return this.hasCarouselWrapper || this.hasWatchDemo || this.hasHighlights || this.hasDescription;
  }

  /**
   * The "carousel-wrapper" unit (Figma: short description directly above the
   * carousel). These two stay grouped so the blurb and the media it introduces
   * read as one block, mirroring v1's `.carousel-wrapper` on `listingDetailV2`.
   */
  get hasCarouselWrapper() {
    return this.hasShortDescription || this.hasCarouselItems;
  }
  get isConsultantListing() {
    return isConsultantListing(this.listing);
  }

  /**
   * Provider Achievements section — mounts `<listing-achievements>` when the
   * consultant listing has earned at least one achievement (Summit tier /
   * Forward Deployed Engineering partner), or the listing has a Partner of the
   * Year award and `FF_TRYBUY_POTY_BADGE` is on.
   * Unlike v1, nothing here is gated behind the FDE badge flag: v2 is already
   * behind its own feature flag and leaves consultant achievements ungated, so
   * PoTY follows suit and needs only its own flag. This mirrors the v2 header
   * award deck, so the deck's scroll-to-achievements jump always has a target.
   */
  get hasProviderAchievements() {
    return selectProviderAchievements(this.listing, FeatureFlags.FF_TRYBUY_POTY_BADGE).length > 0;
  }

  /**
   * Social Impact section — mounts `<listing-social-impact>` when the listing
   * declares at least one social-impact feature (Pledge 1% / Diverse-Owned).
   */
  get hasSocialImpact() {
    return selectSocialImpactFeatures(this.listing).length > 0;
  }

  /**
   * Accessible names for the Achievements / Social Impact regions. Their visible
   * headings render inside the child components' shadow roots, which
   * `aria-labelledby` can't reach, so name the regions with the same i18n strings.
   */
  get achievementsHeading() {
    return i18nStore.getState().i18n?.heading?.providerAchievements?.({
      PROVIDER_NAME: this.providerName
    }) ?? '';
  }
  get socialImpactHeading() {
    return i18nStore.getState().i18n?.heading?.providerImpact?.({
      PROVIDER_NAME: this.providerName
    }) ?? '';
  }
  get providerName() {
    return getListingPublisher(this.listing)?.name ?? '';
  }
  get hasGeographicFocus() {
    const locations = this.listing?.consultantLocations?.locations;
    return this.isConsultantListing && Array.isArray(locations) && locations.length > 0;
  }
  get hasLanguages() {
    const languages = selectExtensionListing(this.listing)?.languages;
    return this.isConsultantListing && Array.isArray(languages) && languages.length > 0 && !!this.i18n?.listingDetails?.badges;
  }

  /**
   * Short description (`listing.description`) — v1's carousel-wrapper
   * "listing-description" blurb that sits directly above the carousel. This is
   * NOT `fullDescription`; the two are distinct fields (see `hasDescription`).
   */
  get hasShortDescription() {
    return (this.listing?.description?.trim().length ?? 0) > 0;
  }
  get formattedShortDescription() {
    return parseNewLines(this.listing?.description || '');
  }

  /**
   * Full description (`listing.fullDescription`) — the "Additional Details"
   * block brought over from the More Details tab (Figma node 7741:119848). It
   * renders at the BOTTOM of the Features section, below the numbered cards,
   * with no heading of its own.
   *
   * v2: v1 gates the full description to consultant listings
   * (`isConsultantListing && ...`); the v2 design surfaces it for every
   * listing. Guarded by a test asserting the description renders for a
   * NON-consultant listing (where v1 would hide it).
   */
  get hasDescription() {
    return (this.listing?.fullDescription?.trim().length ?? 0) > 0;
  }
  get formattedDescription() {
    return parseNewLines(this.listing?.fullDescription || '');
  }

  /**
   * Category / competency pill groups, relocated from v1's left info-wrapper
   * (the former `listing/titledGroups`) to the bottom of the Overview v2 tab
   * per the Figma.
   *
   * These getters were PORTED from the former `listing/titledGroups` so the
   * badge/label derivation (incl. JP localization via the `i18n.oaf.*` maps)
   * stays identical to what v1 rendered. v1 `listing/titledGroups` has since
   * been deleted (W-24557579); these getters are now the sole source of the
   * pill groups. See docs/W-23636498-overviewv2-changes.md.
   */

  /**
   * Whether the Categories section has any pill group to render. Mirrors the
   * two `isConsultantListing` branches in the template: without this guard the
   * section heading renders over empty space when a listing has no categories.
   */
  get hasCategories() {
    const groups = this.categoryGroups;
    if (this.isConsultantListing) {
      return groups.industryCompetencies.length > 0 || groups.productCompetencies.length > 0 || groups.geographicFocus.length > 0 || groups.languages.length > 0;
    }
    return groups.businessNeeds.length > 0 || groups.industries.length > 0 || groups.targetPersonas.length > 0 || groups.supportedFeatures.length > 0;
  }
  recomputeBadges() {
    this.categoryGroups = {
      businessNeeds: this.buildBusinessNeedsBadges(),
      industries: this.buildIndustryBadges(),
      targetPersonas: this.buildTargetPersonaBadges(),
      supportedFeatures: this.buildSupportedFeatureBadges(),
      supportedFeaturesTooltip: this.listing ? generateBadgeTooltips(selectSupportFeatures(this.listing), this.forceListing) : [],
      industryCompetencies: this.buildIndustryCompetenciesBadges(),
      productCompetencies: this.buildProductCompetenciesBadges(),
      geographicFocus: this.buildGeographicFocusBadges(),
      languages: this.buildLanguageBadges()
    };
    this.recomputeCompatibilityBadges();
  }

  /**
   * Compatibility section (Required / Compatible Salesforce Products, Editions,
   * Languages, Accessibility report), moved here from the Specs tab beneath
   * Categories. Gated on FF_TRYBUY_LISTING_DETAIL_V2_POST_DF (off, Specs renders it) and
   * to non-consultant listings, matching where the Specs tab rendered it. Reads the
   * same cached lists the template's per-row guards do.
   */
  get hasCompatibility() {
    const {
      requiredProducts,
      compatibleWith,
      salesforceEditions,
      supportedLanguages
    } = this.compatibilityBadges;
    // FF_TRYBUY_LISTING_DETAIL_V2_POST_DF off: Compatibility stays on the Specs tab.
    return FeatureFlags.FF_TRYBUY_LISTING_DETAIL_V2_POST_DF && !this.isConsultantListing && (requiredProducts.length > 0 || compatibleWith.length > 0 || salesforceEditions.length > 0 || supportedLanguages.length > 0 || !!this.accessibleSolutionURL);
  }
  recomputeCompatibilityBadges() {
    const labels = this.i18n?.listingDetails?.productsSupported ?? {};
    const forceListing = this.forceListing;
    this.compatibilityBadges = {
      requiredProducts: selectRequiredProducts(forceListing).map(key => generateSimpleBadge(labels[key] || key)),
      compatibleWith: selectCompatibleWith(forceListing).map(key => generateSimpleBadge(labels[key] || key)),
      salesforceEditions: selectSalesforceEditions(forceListing).map(key => generateLocalizedBadge(key)),
      supportedLanguages: selectLanguages(forceListing).map(key => generateLocalizedBadge(key))
    };
  }

  // Fall back to the pre-V2 headings for locales that don't carry the V2 labels yet.
  get requiredProductsLabel() {
    return this.i18n?.compatibility?.requiredProductsGroupV2 || this.i18n?.compatibility?.requiredProductsGroup || '';
  }
  get compatibleWithLabel() {
    return this.i18n?.compatibility?.compatibleWithV2 || this.i18n?.compatibility?.compatibleWith || '';
  }
  get requiredProductsTooltipAriaLabel() {
    return buildTooltipAriaLabel(this.requiredProductsLabel, this.i18n?.shared?.moreInfo ?? '');
  }
  get compatibleWithTooltipAriaLabel() {
    return buildTooltipAriaLabel(this.compatibleWithLabel, this.i18n?.shared?.moreInfo ?? '');
  }
  get accessibleSolutionURL() {
    return this.forceListing?.accessibleSolutionURL ?? '';
  }
  get accessibilityLinkAriaLabel() {
    const label = this.i18n?.compatibility?.accessibilityReportLink ?? '';
    const formatter = i18nStore.getState().i18n?.listingCard?.opensInNewTabLabel;
    return formatter?.({
      TITLE: label
    }) || label;
  }

  // --- Non-consultant groups ---

  buildIndustryBadges() {
    const labels = this.i18n?.oaf?.supportedIndustries || {};
    const supportedIndustries = this.forceListing?.supportedIndustries;
    if (supportedIndustries && supportedIndustries.length !== 0) {
      return supportedIndustries.map(key => generateSimpleBadge(labels[key] || key));
    }
    return this.forceListing?.industries?.map(key => generateSimpleBadge(labels[key] || key)) ?? [];
  }
  buildBusinessNeedsBadges() {
    if (!this.listing?.businessNeeds) {
      return [];
    }
    const labels = this.i18n?.oaf?.category || {};
    const l1Categories = Object.keys(schemas['listing/ListingCategories'].get().properties);
    const selectedL1Categories = l1Categories.filter(l1Cat => this.listing.businessNeeds[l1Cat]?.isSelected);
    return [
    // Level 1 (L1) categories
    ...(selectedL1Categories.map(l1Category => generateSimpleBadge(labels[l1Category] || l1Category)) ?? []),
    // Level 2 (L2) sub-categories
    ...l1Categories.reduce((accumulator, l1Category) => {
      const l2Categories = this.listing.businessNeeds[l1Category]?.categories ?? [];
      for (const l2Category of l2Categories) {
        accumulator.push(generateSimpleBadge(labels[l2Category] || l2Category));
      }
      return accumulator;
    }, [])];
  }

  // New in v2: Target Personas read from the Force listing extension's
  // `targetUserPersona` array (same extension as `supportedIndustries`), labeled
  // through the existing `oaf.targetUserPersona` value map.
  buildTargetPersonaBadges() {
    const personas = this.forceListing?.targetUserPersona;
    if (!personas || personas.length === 0) {
      return [];
    }
    const labels = this.i18n?.oaf?.targetUserPersona || {};
    return personas.map(key => generateSimpleBadge(labels[key] || key));
  }
  buildSupportedFeatureBadges() {
    if (!this.listing) {
      return [];
    }
    return selectSupportFeatures(this.listing).map(key => generateLocalizedBadge(key));
  }

  // --- Consultant groups ---

  buildIndustryCompetenciesBadges() {
    if (!this.isConsultantListing) {
      return [];
    }
    const industryCompetencies = this.listing?.consultantCompetencies?.industryCompetencies;
    if (!industryCompetencies || industryCompetencies.length === 0) {
      return [];
    }
    // Sort alphabetically before generating badges.
    return [...industryCompetencies].sort((a, b) => a.localeCompare(b)).map(value => generateSimpleBadge(value));
  }
  buildProductCompetenciesBadges() {
    const productCompetencies = this.listing?.consultantCompetencies?.productCompetencies;
    if (!productCompetencies || productCompetencies.length === 0) {
      return [];
    }
    // Sort alphabetically before generating badges.
    return [...productCompetencies].sort((a, b) => a.localeCompare(b)).map(value => generateSimpleBadge(value));
  }
  get geographicFocusTooltip() {
    return this.i18n?.listingDetails?.geographicFocusTooltip;
  }

  // Center the popover arrow under the info icon at every breakpoint (matches
  // the metadata card and the specs tooltips). Pairs with
  // `--arrow-horizontal-center` on `.pill-group` in the css; wds-popover's
  // clampToViewport keeps the centered panel from overflowing the viewport.
  get infoIconTooltipOrientation() {
    return {
      vertical: PopoverVerticalOrientation.BOTTOM,
      horizontal: PopoverHorizontalOrientation.CENTER
    };
  }

  // Geographic Focus + Supported Languages rendered as pills at the bottom
  // (per Figma), reusing the existing `geographicFocusCountries` / `languagesList`
  // derivations but split into individual badges.
  buildGeographicFocusBadges() {
    if (!this.hasGeographicFocus) {
      return [];
    }
    const locations = this.listing?.consultantLocations?.locations || [];
    const countries = this.i18n?.location?.countries;
    const priorityOrder = ['US', 'CA', 'JP'];
    const getName = loc => countries?.[loc.countryCode] || loc.countryName;
    return [...locations].sort((a, b) => {
      const aIdx = priorityOrder.indexOf(a.countryCode);
      const bIdx = priorityOrder.indexOf(b.countryCode);
      if (aIdx !== -1 && bIdx !== -1) {
        return aIdx - bIdx;
      }
      if (aIdx !== -1) {
        return -1;
      }
      if (bIdx !== -1) {
        return 1;
      }
      return getName(a).localeCompare(getName(b));
    }).map(loc => generateSimpleBadge(getName(loc)));
  }
  buildLanguageBadges() {
    if (!this.hasLanguages) {
      return [];
    }
    const languages = selectExtensionListing(this.listing)?.languages || [];
    const badges = this.i18n?.listingDetails?.badges;
    if (!badges) {
      return [];
    }
    return [...languages].map(code => badges[code] || code).sort((a, b) => a.localeCompare(b)).map(label => generateSimpleBadge(label));
  }
  get hasWatchDemo() {
    return this.hasDemoItem;
  }
  get watchDemoHeading() {
    return i18nStore.getState().i18n.listingDetails.carousel.seeHowWorks({
      LISTING_NAME: this.listing?.name || ''
    });
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(OverviewV2, {
  publicProps: {
    listing: {
      config: 3
    },
    carouselItems: {
      config: 0
    },
    isMobilePhone: {
      config: 0
    },
    hasDemoLeadsEnabled: {
      config: 0
    },
    hasDemoItem: {
      config: 0
    },
    watchDemoUrl: {
      config: 0
    },
    watchDemoPreviewUrl: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  },
  fields: ["categoryGroups", "compatibilityBadges"]
});
const __lwc_component_class_internal = _registerComponent(OverviewV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;